import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Trophy, Award, Crown, MapPin, Calendar, Star, ShieldCheck } from 'lucide-react';

const EquestrianCenter: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Royal Stables";
    
    return (
        <div className="min-h-screen bg-[#0f172a] font-serif text-slate-200 selection:bg-[#d4af37] selection:text-black">
            
            {/* Split Screen Hero */}
            <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen">
                
                {/* Left: Content */}
                <div className="relative flex flex-col justify-between p-12 lg:p-20 order-2 lg:order-1 bg-[#0f172a]">
                    {/* Pattern Overlay */}
                    <div className="absolute inset-0 opacity-5 bg-[radial-gradient(#d4af37_1px,transparent_1px)] bg-[size:20px_20px]"></div>

                    {/* Nav */}
                    <nav className="relative z-10 flex justify-between items-center mb-20">
                         <div className="flex items-center gap-4">
                             <div className="w-12 h-12 border border-[#d4af37] flex items-center justify-center rounded-sm">
                                <Trophy size={24} className="text-[#d4af37]" />
                             </div>
                             <span className="text-xl tracking-widest uppercase text-[#d4af37] font-bold">{schoolName}</span>
                         </div>
                    </nav>

                    {/* Hero Text */}
                    <div className="relative z-10 max-w-lg">
                        <div className="flex items-center gap-4 mb-8">
                            <div className="h-px w-12 bg-[#d4af37]"></div>
                            <span className="text-[#d4af37] text-xs font-sans tracking-[0.3em] uppercase">Est. 1985</span>
                        </div>
                        <h1 className="text-5xl md:text-7xl leading-tight mb-8">
                            Elite <br/> 
                            <span className="italic text-white">Equine</span> <br/>
                            Training.
                        </h1>
                        <p className="font-sans text-slate-400 leading-relaxed mb-12 border-l border-[#d4af37]/30 pl-6">
                            World-class facilities for horse and rider. Specializing in dressage, show jumping, and cross-country excellence.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-6">
                            <Button className="bg-[#d4af37] hover:bg-[#b5952f] text-[#0f172a] font-sans font-bold uppercase tracking-widest h-14 px-8 rounded-none">
                                Book Private Lesson
                            </Button>
                            <Button variant="outline" className="border-slate-600 text-slate-300 hover:text-white hover:border-white font-sans font-bold uppercase tracking-widest h-14 px-8 rounded-none">
                                View Stallions
                            </Button>
                        </div>
                    </div>

                    {/* Footer Info */}
                    <div className="relative z-10 mt-20 flex gap-12 font-sans text-xs uppercase tracking-widest text-slate-500">
                        <div className="flex items-center gap-2">
                            <MapPin size={14} className="text-[#d4af37]" /> London, UK
                        </div>
                        <div className="flex items-center gap-2">
                             <Calendar size={14} className="text-[#d4af37]" /> Open Daily
                        </div>
                    </div>
                </div>

                {/* Right: Image */}
                <div className="relative order-1 lg:order-2 h-[50vh] lg:h-auto overflow-hidden group">
                    <img 
                        src="https://images.unsplash.com/photo-1534008104446-aa4a07a68393?q=80&w=1972&auto=format&fit=crop" 
                        alt="Equestrian"
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"    
                    />
                    <div className="absolute inset-0 bg-[#0f172a]/20 group-hover:bg-transparent transition-colors duration-500"></div>
                </div>
            </div>

            {/* Awards / Lux Section */}
            <section className="bg-[#1e293b] py-24 px-12 border-t border-[#d4af37]/10">
                <div className="max-w-7xl mx-auto text-center mb-16">
                    <Crown size={48} className="text-[#d4af37] mx-auto mb-6" strokeWidth={1} />
                    <h2 className="text-3xl font-serif italic text-white mb-4">A Legacy of Victory</h2>
                    <div className="w-24 h-px bg-[#d4af37] mx-auto opacity-50"></div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-12 max-w-6xl mx-auto">
                    {[
                        { title: "National Champions", desc: "Gold medal winners for 5 consecutive years." },
                        { title: "Expert Stabling", desc: "Premium boarding with 24/7 veterinary care." },
                        { title: "Private Clubhouse", desc: "Exclusive lounge for members and guests." },
                    ].map((item, i) => (
                        <div key={i} className="text-center group p-8 border border-transparent hover:border-[#d4af37]/20 transition-all duration-500">
                             <div className="w-16 h-16 bg-[#0f172a] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-black/20 group-hover:scale-110 transition-transform">
                                <Award size={24} className="text-[#d4af37]" />
                             </div>
                             <h3 className="text-xl font-bold uppercase tracking-widest text-slate-200 mb-4">{item.title}</h3>
                             <p className="font-sans text-slate-400 text-sm leading-relaxed">{item.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

             {/* Footer */}
            <footer className="bg-[#0f172a] py-20 px-12 border-t border-[#d4af37]/20 text-center">
                 <div className="max-w-md mx-auto">
                     <div className="flex justify-center items-center gap-4 mb-8">
                        <Trophy size={32} className="text-[#d4af37]" />
                     </div>
                     <p className="font-sans text-slate-500 text-xs tracking-widest uppercase mb-12">
                         © 2026 {schoolName}. All Rights Reserved.
                     </p>
                     <div className="flex justify-center gap-8">
                         <Button variant="ghost" className="text-slate-400 hover:text-[#d4af37] uppercase tracking-widest text-xs">Contact</Button>
                         <Button variant="ghost" className="text-slate-400 hover:text-[#d4af37] uppercase tracking-widest text-xs">Location</Button>
                         <Button variant="ghost" className="text-slate-400 hover:text-[#d4af37] uppercase tracking-widest text-xs">Events</Button>
                     </div>
                 </div>
            </footer>
        </div>
    );
};

export default EquestrianCenter;
